import React, { memo, useCallback, useMemo, useState } from 'react';
/** ############################### REDUX ############################### */
import { batch } from 'react-redux';
import { loadAnalysisAction } from '../../../redux/slices/loadingAnalyses';
import { isAnalysisLoadingSelector } from '../../../redux/selectors/loadingAnalyses';
/** ############################## APOLLO ############################### */
import { useQuery } from '@apollo/client';
import { GET_ANALYSIS } from '../../../utils/apollo/analysis';
import {
  ErrorType,
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
/** ############################## UI ############################### */
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { populateAnalysisThunk } from '../../../redux/slices/analyses';
import { assertNotNil } from '../../../utils/types';
import { Chart, ChartLoader, PermissionErrorChart } from '../../Chart';
import { reportViewed } from '../../../utils/analytics/events/report/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { ChartMode } from '../../Chart/types';

interface IProps {
  analysisId: string;
  entity: any;
  isWorkspaceEditing: boolean;
  isSelected?: boolean;
  isDragging?: boolean;
}

const mode = ChartMode.View;

export default memo(function WorkspaceItem({
  analysisId,
  entity,
  isWorkspaceEditing,
  isSelected,
  isDragging,
}: IProps) {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const [isPermissionError, setIsPermissionError] = useState(false);
  const isAnalysisLoadingMemoizedSelector = useMemo(
    isAnalysisLoadingSelector,
    [],
  );

  const isAnalysisLoading = useAppSelector((state) =>
    isAnalysisLoadingMemoizedSelector(state, analysisId),
  );

  const loadAnalysisInStore = useCallback(
    ({ analysis }: IAnalysisQuery) => {
      assertNotNil(analysis);
      batch(() => {
        dispatch(
          populateAnalysisThunk({
            analysis,
          }),
        );
        dispatch(loadAnalysisAction(analysis.id));
      });
      track(reportViewed(analysis));
    },
    [dispatch, track],
  );

  useQuery<IAnalysisQuery, IAnalysisQueryVariables>(GET_ANALYSIS, {
    variables: {
      analysisId,
      refresh: false,
    },
    onCompleted: loadAnalysisInStore,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      for (const graphQLError of error.graphQLErrors) {
        if (
          graphQLError.extensions?.classification ===
          ErrorType.MissingPermission
        ) {
          setIsPermissionError(true);
          dispatch(loadAnalysisAction(analysisId));
        }
      }
    },
    // if we are in edit mode, once we drag & drop,
    // all edits would result in the original state unless we skip the query
    skip: isWorkspaceEditing,
  });

  if (isAnalysisLoading) {
    return <ChartLoader display={entity.display} mode={mode} />;
  }

  if (isPermissionError) {
    return <PermissionErrorChart mode={mode} />;
  }

  return (
    <Chart
      analysisId={analysisId}
      mode={mode}
      isDragSelected={isSelected}
      isDragging={isDragging}
      optionsEnabled={!isWorkspaceEditing}
    />
  );
});
